import { RegisteredStarComponent } from '../../components/registered-star/registered-star.component';
import { countPodiums } from '../../models/podium.models';
import { Component, ChangeDetectionStrategy, inject, signal, computed, input, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HumanService } from '../../services/human.service';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { ResultStatusPipe } from '../../pipes/result-status.pipe';
import { DisciplinePipe } from '../../pipes/discipline.pipe';
import { RouterModule } from '@angular/router';
import { BackNavigationDirective } from '../../directives/back-navigation.directive';
import { Event, Human } from '../../models/data.models';
import { HumanProfileResult } from '../../models/human-profile.models';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { HumanAvatarComponent } from '../../components/human-avatar/human-avatar.component';
import { HumanProfileStatsComponent } from '../../components/human-profile-stats/human-profile-stats.component';
import { HumanProfileDataService } from '../../services/human-profile-data.service';
import { StyledSelectComponent, StyledSelectOption } from '../../components/styled-select/styled-select.component';
import { DistancePipe } from '../../pipes/distance.pipe';
import { normalizeResultDiscipline } from '../../models/discipline.models';
import { AuthService } from '../../services/auth.service';
import { DogService } from '../../services/dog.service';
import { Dog } from '../../models/data.models';
import { ClubPickerComponent, ClubSelection } from '../../components/club-picker/club-picker.component';
import { BreedNamePipe } from '../../pipes/breed-name.pipe';
import { currentSeasonStartYear, defaultSeason, seasonLabel, seasonStartYear } from '../../utils/season';

@Component({
  selector: 'app-human-profile',
  standalone: true,
  imports: [RegisteredStarComponent, CommonModule, FormsModule, TranslatePipe, DisplayValuePipe, ResultStatusPipe, DisciplinePipe, DistancePipe, BreedNamePipe, RouterModule, BackNavigationDirective, BrandedLoaderComponent, HumanAvatarComponent, StyledSelectComponent, HumanProfileStatsComponent, ClubPickerComponent],
  templateUrl: './human-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HumanProfileComponent {
  humanService = inject(HumanService);
  translationService = inject(TranslationService);
  private readonly profileDataService = inject(HumanProfileDataService);
  private readonly authService = inject(AuthService);
  private readonly dogService = inject(DogService);
  readonly isAdmin = this.authService.isAdmin;
  adminEditOpen = signal(false);
  adminSaving = signal(false);
  adminError = signal(false);
  /** Edit form of the displayed profile; humanId ties it to that profile. */
  adminDraft = signal({ humanId: '', name: '', age: 0, gender: 'Other' as Human['gender'], nationality: '', imageUrl: '', coverImageUrl: '', clubName: '' });
  /** Club typed in the edit form; null while the field is untouched. */
  adminClubSelection = signal<ClubSelection | null>(null);
  adminDogs = signal<Dog[]>([]);
  adminMergeQuery = signal('');
  adminMergeCandidates = signal<Human[]>([]);
  adminMergeSearching = signal(false);
  adminMerging = signal(false);
  adminDogMergeSourceId = signal('');
  adminDogMergeTargetId = signal('');

  async openAdminEdit(): Promise<void> {
    const human = this.human();
    if (!human || !this.isAdmin()) return;
    let dogs: Dog[];
    try {
      dogs = await this.dogService.getDogsByOwner(human.id);
    } catch {
      this.adminError.set(true);
      return;
    }
    // The visitor may have moved to another profile while the dogs loaded.
    if (this.human()?.id !== human.id) return;
    this.adminDraft.set({ humanId: human.id, name: human.name, age: human.age, gender: human.gender,
      nationality: human.nationality || '', imageUrl: human.imageUrl || '', coverImageUrl: human.coverImageUrl || '',
      clubName: human.club || '' });
    this.adminClubSelection.set(null);
    this.adminDogs.set(dogs);
    this.adminError.set(false);
    this.adminMergeQuery.set('');
    this.adminMergeCandidates.set([]);
    this.adminDogMergeSourceId.set('');
    this.adminDogMergeTargetId.set('');
    this.adminEditOpen.set(true);
  }

  async searchAdminMergeCandidates(): Promise<void> {
    const human = this.human();
    const query = this.adminMergeQuery().trim();
    if (!human || query.length < 3 || !this.isAdmin()) return;
    this.adminMergeSearching.set(true);
    try { this.adminMergeCandidates.set(await this.humanService.searchAdminMergeCandidates(query, human.id)); }
    catch { this.adminError.set(true); }
    finally { this.adminMergeSearching.set(false); }
  }

  async mergeAdminProfile(source: Human): Promise<void> {
    const target = this.human();
    if (!target || !this.isAdmin() || this.adminMerging()) return;
    const confirmed = window.confirm(this.translationService.translate('humanProfile.admin.confirmMergeProfile', source.name, target.name));
    if (!confirmed) return;
    this.adminMerging.set(true); this.adminError.set(false);
    try {
      await this.humanService.mergeHumanProfilesAsAdmin(source.id, target.id);
      this.profileDataService.invalidate(undefined, target.id);
      this.adminMergeCandidates.set([]);
      const dogs = await this.dogService.getDogsByOwner(target.id);
      if (this.humanId() === target.id) this.adminDogs.set(dogs);
      await this.refreshIfCurrent(target.id);
    } catch { this.adminError.set(true); }
    finally { this.adminMerging.set(false); }
  }

  async mergeAdminDogs(): Promise<void> {
    const source = this.adminDogs().find(dog => dog.id === this.adminDogMergeSourceId());
    const target = this.adminDogs().find(dog => dog.id === this.adminDogMergeTargetId());
    const human = this.human();
    if (!source || !target || !human || source.id === target.id || !this.isAdmin() || this.adminMerging()) return;
    const confirmed = window.confirm(this.translationService.translate('humanProfile.admin.confirmMergeDogs', source.name, target.name));
    if (!confirmed) return;
    this.adminMerging.set(true); this.adminError.set(false);
    try {
      await this.dogService.mergeDogsAsAdmin(source.id, target.id);
      const dogs = await this.dogService.getDogsByOwner(human.id);
      if (this.humanId() === human.id) this.adminDogs.set(dogs);
      this.adminDogMergeSourceId.set(''); this.adminDogMergeTargetId.set('');
      this.profileDataService.invalidate(undefined, human.id);
      await this.refreshIfCurrent(human.id);
    } catch { this.adminError.set(true); }
    finally { this.adminMerging.set(false); }
  }

  updateAdminDraft(field: string, value: unknown): void {
    this.adminDraft.update(draft => ({ ...draft, [field]: value }));
  }

  updateAdminDog(index: number, field: keyof Dog, value: unknown): void {
    this.adminDogs.update(dogs => dogs.map((dog, i) => i === index ? { ...dog, [field]: value } : dog));
  }

  addAdminDog(): void {
    const human = this.human();
    if (!human) return;
    this.adminDogs.update(dogs => [...dogs, { id: '', ownerId: human.id, name: '', age: 0, breed: '', gender: 'Male' }]);
  }

  async deleteAdminDog(index: number): Promise<void> {
    const dog = this.adminDogs()[index];
    if (!dog || !window.confirm(this.translationService.translate('humanProfile.admin.confirmDeleteDog',
      dog.name || this.translationService.translate('humanProfile.admin.thisDog')))) return;
    try {
      if (dog.id) await this.dogService.deleteDog(dog.id);
      this.adminDogs.update(dogs => dogs.filter((_, i) => i !== index));
    } catch {
      this.adminError.set(true);
    }
  }

  async saveAdminProfile(): Promise<void> {
    const human = this.human();
    const draft = this.adminDraft();
    // Never write a form opened on another profile.
    if (!human || draft.humanId !== human.id || !this.isAdmin() || !draft.name.trim()) return;
    const clubSelection = this.adminClubSelection();
    if (clubSelection && !clubSelection.resolved) {
      this.adminError.set(true);
      return;
    }
    this.adminSaving.set(true); this.adminError.set(false);
    try {
      await this.humanService.updateHuman(human.id, { name: draft.name.trim(), age: Number(draft.age), gender: draft.gender,
        nationality: draft.nationality as Human['nationality'], imageUrl: draft.imageUrl, coverImageUrl: draft.coverImageUrl });
      if (clubSelection && clubSelection.name !== (human.club ?? '')) {
        await this.humanService.updateHumanClub(human.id, clubSelection.id);
      }
      for (const dog of this.adminDogs()) {
        if (!dog.name.trim()) continue;
        if (dog.id) await this.dogService.updateDog(dog.id, dog);
        else await this.dogService.addDog({ ...dog, ownerId: human.id });
      }
      this.profileDataService.invalidate(undefined, human.id);
      await this.refreshIfCurrent(human.id);
      this.adminEditOpen.set(false);
    } catch { this.adminError.set(true); }
    finally { this.adminSaving.set(false); }
  }

  humanId = input.required<string>({ alias: 'id' });

  human = signal<Human | null>(null);
  humanResults = signal<HumanProfileResult[]>([]);
  participantCounts = signal<Record<string, number>>({});
  categoryParticipantCounts = signal<Record<string, number>>({});
  isLoading = signal(true);
  statsScope = signal<'season' | 'all'>('season');
  selectedSeasonStart = signal(currentSeasonStartYear());
  /** Set once the visitor picks a period; until then the latest season with results is shown. */
  private periodChosen = false;

  availableSeasons = computed(() => {
    const seasons = new Set<number>([currentSeasonStartYear()]);
    this.humanResults().forEach(result => {
      const season = seasonStartYear(result.event?.date);
      if (season !== null) seasons.add(season);
    });
    return Array.from(seasons).sort((a, b) => b - a);
  });

  selectedSeasonLabel = computed(() => seasonLabel(this.selectedSeasonStart()));

  statsPeriodOptions = computed<StyledSelectOption[]>(() => [
    { value: 'all', label: this.translationService.translate('humanProfile.allTime') },
    ...this.availableSeasons().map(year => ({
      value: String(year),
      label: this.translationService.translate('humanProfile.season', seasonLabel(year)),
    })),
  ]);

  statsScopeLabel = computed(() => this.statsScope() === 'all'
    ? this.translationService.translate('humanProfile.allTime')
    : this.translationService.translate('humanProfile.season', this.selectedSeasonLabel()));

  scopedResults = computed(() => {
    if (this.statsScope() === 'all') return this.humanResults();
    return this.humanResults().filter(result => this.isInSeason(result.event?.date, this.selectedSeasonStart()));
  });


  profileDogs = computed(() => {
    const dogs = new Map<string, { id: string; name: string; age?: number; breed?: string; imageUrl?: string; races: number; distance: number; disciplines: { name: string; races: number; distance: number }[] }>();
    this.scopedResults().forEach(result => {
      const dog = result.team?.dog;
      if (!dog) return;
      const current = dogs.get(dog.id) ?? {
        id: dog.id,
        name: dog.name,
        age: dog.age,
        breed: dog.breed,
        imageUrl: dog.imageUrl,
        races: 0,
        distance: 0,
        disciplines: [],
      };
      const distance = result.race?.distanceKm || 0;
      current.races++;
      current.distance += distance;
      const normalized = normalizeResultDiscipline(result.discipline, result.race?.name, result.category);
      const name = normalized === 'Cani-VTT' ? 'VTT'
        : normalized === 'Cani-Trottinette' ? 'Trottinette'
        : normalized === 'Cani-Marche' ? 'Cani-marche'
        : 'Canicross';
      const discipline = current.disciplines.find(item => item.name === name);
      if (discipline) { discipline.races++; discipline.distance += distance; }
      else current.disciplines.push({ name, races: 1, distance });
      dogs.set(dog.id, current);
    });
    return [...dogs.values()].sort((a, b) => b.races - a.races || b.distance - a.distance || a.name.localeCompare(b.name));
  });

  totalRaces = computed(() => this.scopedResults().length);

  resultsBySeason = computed(() => {
    const groups = new Map<number, HumanProfileResult[]>();
    for (const result of this.scopedResults()) {
      const season = seasonStartYear(result.event?.date) ?? -1;
      const group = groups.get(season);
      if (group) group.push(result);
      else groups.set(season, [result]);
    }
    return [...groups.entries()].sort((a, b) => b[0] - a[0]);
  });

  seasonLabel(seasonStart: number): string {
    return seasonStart < 0 ? '—' : seasonLabel(seasonStart);
  }

  podiums = computed(() => {
    const results = this.scopedResults();
    return countPodiums(results);
  });

  hasPodiums = computed(() => {
    const p = this.podiums();
    return p.first > 0 || p.second > 0 || p.third > 0 || p.fourth > 0;
  });

  groupedResults = computed(() => {
    const results = this.scopedResults();
    const counts = this.participantCounts();
    const groups: { event: Event; results: any[] }[] = [];

    results.forEach(res => {
      const event = res.event || { id: 'unknown', name: 'Autre', date: '', location: '' } as Event;
      let group = groups.find(g => g.event.id === event.id);
      if (!group) {
        group = { event, results: [] };
        groups.push(group);
      }
      group.results.push({
        ...res,
        totalParticipants: counts[res.raceId] || 0
      });
    });

    return groups;
  });

  getFlagEmoji = this.humanService.getFlagEmoji;

  /** Sex of a runner as stored (Male, Female, Other), in the page's language. */
  genderLabel(gender: string | undefined): string {
    if (!gender) return '';
    const key = `humanProfile.genders.${gender}`;
    const label = this.translationService.translate(key);
    return label === key ? gender : label;
  }

  private profileLoadVersion = 0;

  constructor() {
    effect(() => {
      const id = this.humanId();
      untracked(() => {
        // Another profile (e.g. opened from the search): drop the state of the previous one.
        this.resetAdminEdit();
        this.periodChosen = false;
        this.statsScope.set('season');
        void this.loadProfile(id);
      });
    });
  }

  /** Reloads a profile after an admin change, unless the visitor has moved to another one. */
  private async refreshIfCurrent(humanId: string): Promise<void> {
    const profileData = await this.profileDataService.getHumanProfile(humanId, true);
    if (this.humanId() === humanId) this.applyProfileData(profileData);
  }

  private resetAdminEdit(): void {
    this.adminEditOpen.set(false);
    this.adminDraft.set({ humanId: '', name: '', age: 0, gender: 'Other', nationality: '', imageUrl: '', coverImageUrl: '', clubName: '' });
    this.adminClubSelection.set(null);
    this.adminDogs.set([]);
    this.adminError.set(false);
    this.adminMergeQuery.set('');
    this.adminMergeCandidates.set([]);
    this.adminDogMergeSourceId.set('');
    this.adminDogMergeTargetId.set('');
  }

  async loadProfile(id: string) {
    const version = ++this.profileLoadVersion;
    const cachedProfile = this.profileDataService.getCachedHumanProfile(id);
    if (cachedProfile) {
      this.applyProfileData(cachedProfile);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    try {
      const profileData = await this.profileDataService.getHumanProfile(id);
      // A newer profile was requested while this one loaded: let it win.
      if (version === this.profileLoadVersion) this.applyProfileData(profileData);
    } catch (err) {
      console.error(err);
    } finally {
      if (version === this.profileLoadVersion) this.isLoading.set(false);
    }
  }

  private applyProfileData(profileData: Awaited<ReturnType<HumanProfileDataService['getHumanProfile']>>): void {
    this.human.set(profileData.human);
    this.humanResults.set(profileData.results);
    if (!this.periodChosen) {
      this.selectedSeasonStart.set(defaultSeason(profileData.results
        .map(result => seasonStartYear(result.event?.date))
        .filter((season): season is number => season !== null)));
    }
    this.participantCounts.set(profileData.participantCounts);
    this.categoryParticipantCounts.set(profileData.categoryParticipantCounts);
  }

  setStatsPeriod(value: string): void {
    this.periodChosen = true;
    if (value === 'all') {
      this.statsScope.set('all');
      return;
    }

    const seasonStart = Number(value);
    if (!Number.isInteger(seasonStart)) return;
    this.selectedSeasonStart.set(seasonStart);
    this.statsScope.set('season');
  }

  formatTime(totalSeconds: number): string {
    if (!totalSeconds || totalSeconds <= 0) return '—';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const pad = (num: number) => num.toString().padStart(2, '0');
    if (hours > 0) {
      return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
    return `${pad(minutes)}m ${pad(seconds)}s`;
  }

  formatDate(isoString: string): string {
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return new Date(isoString).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  private isInSeason(dateString: string | undefined, seasonStart: number): boolean {
    return seasonStartYear(dateString) === seasonStart;
  }
}
